import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Icon, NavRow, Skeleton, Text } from '@fluent/ui';
import { useAuth } from '../auth/authStore.js';
import { supabase } from '../lib/supabase.js';
import { SUBSCRIPTION_KEY, useDerivedSubscriptionStatus, useSubscription } from './hooks.js';
import { isGiftSubscription, type DerivedStatus } from './access.js';
import { CardGlyph, CrownGlyph } from '../profile/icons.js';
import { formatDateDayMonth, toDateLang } from '../lib/dates.js';
import { isNativeApp } from '../native/platform.js';
import './SubscriptionRow.css';

/**
 * SubscriptionRow — the profile's one-glance subscription summary.
 * Replaces the old SubscriptionCard: no actions live here anymore; the row
 * is a door to /subscription where everything (subscribe, cancel, resume,
 * card) happens natively. State skins via the icon tile color + date line.
 *
 * Free tier renders the taller promo variant (perk line + chunky CTA);
 * both the card and the button route to the same place — profile sells,
 * /subscription pays.
 */
export function SubscriptionRow({ onOpen }: { onOpen: () => void }) {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { data: sub, isLoading } = useSubscription();
  const status = useDerivedSubscriptionStatus();
  const queryClient = useQueryClient();
  const syncedOnceRef = useRef(false);

  // Self-heal subscription state on mount (kept verbatim from the old
  // SubscriptionCard): webhooks can miss; every profile visit reconciles
  // our row with Paddle via /api/paddle-sync. Failure is non-fatal.
  useEffect(() => {
    if (!user) return;
    if (syncedOnceRef.current) return;
    syncedOnceRef.current = true;
    let cancelled = false;
    (async () => {
      try {
        const { data: sess } = await supabase.auth.getSession();
        const token = sess.session?.access_token;
        if (!token) return;
        const r = await fetch('/api/paddle-sync', {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        if (r.ok) {
          await queryClient.invalidateQueries({ queryKey: SUBSCRIPTION_KEY(user.id) });
        }
      } catch {
        // Best-effort; cached read remains valid.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, queryClient]);

  const fmt = (iso: string | null | undefined): string =>
    formatDateDayMonth(iso, toDateLang(i18n.language));

  if (isLoading && sub === undefined) {
    return (
      <div className="fluent-subrow__skeleton" aria-busy="true">
        <Skeleton width={40} height={40} />
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <Skeleton width="45%" height={16} />
          <Skeleton width="65%" height={13} />
        </div>
      </div>
    );
  }

  // In the store app a free user sees no "become Pro" promo at all: it would
  // be a sales pitch for something the app is not allowed to sell
  // (DECISIONS 2026-09-21). Subscribers still see their status row.
  if (status === 'none' && isNativeApp()) return null;

  if (status === 'none') {
    return (
      <div className="fluent-subrow__promo">
        <div className="fluent-subrow__promo-head">
          <span className="fluent-subrow__tile" data-state="promo" aria-hidden="true">
            <Icon size={24}>
              <CrownGlyph />
            </Icon>
          </span>
          <div style={{ minWidth: 0 }}>
            <Text weight="bold" size="lg" style={{ display: 'block' }}>
              {t('profile.subrow.free_title')}
            </Text>
            <Text tone="muted" size="sm" style={{ display: 'block' }}>
              {t('profile.subrow.free_sub')}
            </Text>
          </div>
        </div>
        <Button variant="primary" size="lg" fullWidth onClick={onOpen}>
          {t('profile.subrow.free_cta')}
        </Button>
      </div>
    );
  }

  const trialDate = sub?.trial_ends_at ?? sub?.current_period_ends_at;
  const periodDate = sub?.current_period_ends_at;
  const line: Record<Exclude<DerivedStatus, 'none'>, { title: string; sub: string }> = {
    trialing: {
      title: t('profile.subrow.trial_title'),
      sub: t('profile.subrow.trial_sub', { date: fmt(trialDate) }),
    },
    active: {
      title: t('profile.subrow.active_title'),
      sub: t('profile.subrow.active_sub', { date: fmt(periodDate) }),
    },
    cancelling: {
      title: t('profile.subrow.cancelling_title'),
      sub: t('profile.subrow.cancelling_sub', { date: fmt(periodDate) }),
    },
    past_due: {
      title: t('profile.subrow.past_due_title'),
      sub: t('profile.subrow.past_due_sub'),
    },
    expired: {
      title: t('profile.subrow.expired_title'),
      sub: t('profile.subrow.expired_sub'),
    },
  };
  const { title, sub: subLine } = isGiftSubscription(sub)
    ? { title: t('profile.subrow.active_title'), sub: t('profile.subrow.gift_sub') }
    : line[status];

  return (
    <NavRow
      aria-label={t('profile.subrow.aria')}
      icon={
        <span className="fluent-subrow__tile" data-state={status} aria-hidden="true">
          <Icon size={24}>{status === 'past_due' ? <CardGlyph /> : <CrownGlyph />}</Icon>
        </span>
      }
      title={title}
      sub={subLine}
      onPress={onOpen}
    />
  );
}
